/* ============================================================================
   Competition configuration. This file is the whole editable surface of the
   product: change a callsign, a model id, a venue or the token universe here
   and every panel follows. Nothing below is referenced by literal anywhere else.
   ========================================================================== */

import type { Agent, Chain, House, RailAsset, Vendor } from '../core/types'

export const HOUSES: Record<string, House> = {
  pumpfun: {
    id: 'pumpfun',
    name: 'PUMP.FUN',
    tag: 'SOLANA LAUNCH DESK',
    blurb: 'Bonding-curve launches, fast rotations, survives on being early.',
    color: 'var(--house-pump)',
  },
  fomo: {
    id: 'fomo',
    name: 'FOMO',
    tag: 'BASE MOMENTUM DESK',
    blurb: 'Momentum continuation on established liquidity, sizes into strength.',
    color: 'var(--house-fomo)',
  },
}

export const VENDORS: Record<string, Vendor> = {
  anthropic: { id: 'anthropic', name: 'Anthropic', short: 'CLAUDE', color: 'var(--vendor-anthropic)' },
  openai: { id: 'openai', name: 'OpenAI', short: 'GPT', color: 'var(--vendor-openai)' },
}

/* Colour slots are a validated categorical palette in FIXED order. The order is
   the colourblind-safety mechanism, so reassign agents to slots only after
   re-running the palette validator (see README). Dash patterns are secondary
   encoding: identity is never carried by colour alone. */
export const AGENTS: Agent[] = [
  {
    id: 'ember',
    name: 'EMBER',
    handle: '@ember_pf',
    house: 'pumpfun',
    vendor: 'anthropic',
    model: 'claude-opus-5',
    color: 'var(--series-1)',
    dash: [],
    doctrine: 'Enters only where the exit is already mapped. Cuts on thesis break, not on price.',
  },
  {
    id: 'halcyon',
    name: 'HALCYON',
    handle: '@halcyon_fomo',
    house: 'fomo',
    vendor: 'anthropic',
    model: 'claude-opus-5',
    color: 'var(--series-2)',
    dash: [],
    doctrine: 'Buys the second leg, never the first. Treats drawdown as information.',
  },
  {
    id: 'hydra',
    name: 'HYDRA',
    handle: '@hydra_pf',
    house: 'pumpfun',
    vendor: 'openai',
    model: 'gpt-5',
    color: 'var(--series-3)',
    dash: [7, 4],
    doctrine: 'Wide net, small tickets, ruthless cull. Expects most of them to fail.',
  },
  {
    id: 'midas',
    name: 'MIDAS',
    handle: '@midas_fomo',
    house: 'fomo',
    vendor: 'openai',
    model: 'gpt-5',
    color: 'var(--series-4)',
    dash: [7, 4],
    doctrine: 'Concentrates hard when flow confirms. One idea at a time, sized loud.',
  },
]

export const AGENT_BY_ID: Record<string, Agent> = Object.fromEntries(AGENTS.map((a) => [a.id, a]))

/* The chains agents trade on. This is a registry, not the price rail: Base is
   a first-class venue here even though it has no coin to quote. */
export const CHAINS: Chain[] = [
  { id: 'solana', name: 'Solana', short: 'SOL', symbol: 'SOL' },
  { id: 'ethereum', name: 'Ethereum', short: 'ETH', symbol: 'ETH' },
  { id: 'base', name: 'Base', short: 'BASE', symbol: null },
]

/* What the top rail quotes. Base has no native token -- it settles in ETH --
   so it gets no slot rather than a made-up index. Add one here if that ever
   changes; the rail is laid out from this list. */
export const PRICE_RAIL: RailAsset[] = [
  { symbol: 'SOL', chain: 'solana', label: 'SOL', note: 'solana / usd' },
  { symbol: 'ETH', chain: 'ethereum', label: 'ETH', note: 'ethereum / usd' },
]

/** Base tokens priced live, so the round can anchor them to real prices even
    though none of them belongs on the rail. */
export const BASE_LIVE_TOKENS = ['AERO', 'BRETT', 'DEGEN'] as const

export interface Market {
  symbol: string
  name: string
  chain: 'solana' | 'ethereum' | 'base'
  /** Venue an order in this market routes to. */
  venue: string
  seed: number
  /** Annualised vol used by the simulator, and a rough realism anchor. */
  vol: number
  liquidity: number
  house: 'pumpfun' | 'fomo' | 'both'
}

/* The tradable universe. Majors are quoted on the rail; the rest is what the
   agents actually fight over. */
export const MARKETS: Market[] = [
  { symbol: 'SOL', name: 'Solana', chain: 'solana', venue: 'Jupiter', seed: 214.4, vol: 0.62, liquidity: 9.2e8, house: 'both' },
  { symbol: 'ETH', name: 'Ethereum', chain: 'ethereum', venue: 'Uniswap V4', seed: 3418, vol: 0.45, liquidity: 1.4e9, house: 'both' },
  { symbol: 'WIF', name: 'dogwifhat', chain: 'solana', venue: 'Raydium', seed: 1.82, vol: 1.65, liquidity: 4.1e7, house: 'pumpfun' },
  { symbol: 'BONK', name: 'Bonk', chain: 'solana', venue: 'Raydium', seed: 0.0000241, vol: 1.72, liquidity: 3.6e7, house: 'pumpfun' },
  { symbol: 'POPCAT', name: 'Popcat', chain: 'solana', venue: 'Raydium', seed: 0.412, vol: 1.9, liquidity: 1.8e7, house: 'pumpfun' },
  { symbol: 'GIGA', name: 'Gigachad', chain: 'solana', venue: 'pump.fun', seed: 0.0318, vol: 2.4, liquidity: 6.2e6, house: 'pumpfun' },
  { symbol: 'RETARDIO', name: 'Retardio', chain: 'solana', venue: 'pump.fun', seed: 0.0906, vol: 2.6, liquidity: 4.4e6, house: 'pumpfun' },
  { symbol: 'MOONPIG', name: 'Moonpig', chain: 'solana', venue: 'pump.fun', seed: 0.00427, vol: 3.1, liquidity: 1.9e6, house: 'pumpfun' },
  { symbol: 'AERO', name: 'Aerodrome', chain: 'base', venue: 'Aerodrome', seed: 1.12, vol: 1.15, liquidity: 5.5e7, house: 'fomo' },
  { symbol: 'BRETT', name: 'Brett', chain: 'base', venue: 'Aerodrome', seed: 0.0721, vol: 1.78, liquidity: 2.8e7, house: 'fomo' },
  { symbol: 'DEGEN', name: 'Degen', chain: 'base', venue: 'Uniswap V4', seed: 0.00614, vol: 2.05, liquidity: 1.1e7, house: 'fomo' },
  { symbol: 'TOSHI', name: 'Toshi', chain: 'base', venue: 'Aerodrome', seed: 0.000105, vol: 2.2, liquidity: 8.4e6, house: 'fomo' },
  { symbol: 'HIGHER', name: 'Higher', chain: 'base', venue: 'FOMO', seed: 0.0139, vol: 2.45, liquidity: 3.1e6, house: 'fomo' },
  { symbol: 'PEPE', name: 'Pepe', chain: 'ethereum', venue: 'Uniswap V4', seed: 0.00000842, vol: 1.55, liquidity: 9.1e7, house: 'both' },
  { symbol: 'MOG', name: 'Mog Coin', chain: 'ethereum', venue: 'Uniswap V4', seed: 0.00000122, vol: 1.98, liquidity: 2.2e7, house: 'both' },
]

export const MARKET_BY_SYMBOL: Record<string, Market> = Object.fromEntries(
  MARKETS.map((m) => [m.symbol, m]),
)

/** Rules of the round. */
export const RULES = {
  round: 1,
  seedCapital: 500,
  /** Length of a round in hours. */
  durationHours: 72,
  maxOpenPositions: 6,
  maxPositionPctOfEquity: 26,
  /** Round-trip cost floor, in percent of notional, before price impact. */
  baseFeePct: 0.3,
  /** Flat network + priority cost per fill. At small ticket sizes this, not
      price impact, is what actually eats the edge. */
  gasPerFillUsd: 0.05,
  /** Below this a fill is not worth the gas. */
  minTicketUsd: 12,
} as const
